// FIXME: Update this file to be type safe and remove this and next line
// @ts-strict-ignore
import { Directive } from "@angular/core";
import { Router } from "@angular/router";
import { firstValueFrom } from "rxjs";

import { ApiService } from "@bitwarden/common/abstractions/api.service";
import { PolicyService } from "@bitwarden/common/admin-console/abstractions/policy/policy.service.abstraction";
import { MasterPasswordPolicyOptions } from "@bitwarden/common/admin-console/models/domain/master-password-policy-options";
import { AccountService } from "@bitwarden/common/auth/abstractions/account.service";
import { MasterPasswordApiService } from "@bitwarden/common/auth/abstractions/master-password-api.service.abstraction";
import { UserVerificationService } from "@bitwarden/common/auth/abstractions/user-verification/user-verification.service.abstraction";
import { VerificationType } from "@bitwarden/common/auth/enums/verification-type";
import { PasswordRequest } from "@bitwarden/common/auth/models/request/password.request";
import { getUserId } from "@bitwarden/common/auth/services/account.service";
import { Verification } from "@bitwarden/common/auth/types/verification";
import { PreloginRequest } from "@bitwarden/common/models/request/prelogin.request";
import { InternalMasterPasswordServiceAbstraction } from "@bitwarden/common/key-management/master-password/abstractions/master-password.service.abstraction";
import { I18nService } from "@bitwarden/common/platform/abstractions/i18n.service";
import { LogService } from "@bitwarden/common/platform/abstractions/log.service";
import { MessagingService } from "@bitwarden/common/platform/abstractions/messaging.service";
import { PlatformUtilsService } from "@bitwarden/common/platform/abstractions/platform-utils.service";
import { EncString } from "@bitwarden/common/platform/models/domain/enc-string";
import { MasterKey, UserKey } from "@bitwarden/common/types/key";
import { DialogService, ToastService } from "@bitwarden/components";
import { KdfConfigService, KeyService } from "@bitwarden/key-management";

import { ChangePasswordComponent as BaseChangePasswordComponent } from "./change-password.component";

@Directive()
export class UpdatePasswordComponent extends BaseChangePasswordComponent {
  hint: string;
  key: string;
  enforcedPolicyOptions: MasterPasswordPolicyOptions;
  showPassword = false;
  currentMasterPassword: string;

  onSuccessfulChangePassword: () => Promise<void>;

  constructor(
    protected router: Router,
    i18nService: I18nService,
    platformUtilsService: PlatformUtilsService,
    policyService: PolicyService,
    keyService: KeyService,
    messagingService: MessagingService,
    private masterPasswordApiService: MasterPasswordApiService,
    private userVerificationService: UserVerificationService,
    private logService: LogService,
    dialogService: DialogService,
    kdfConfigService: KdfConfigService,
    masterPasswordService: InternalMasterPasswordServiceAbstraction,
    accountService: AccountService,
    toastService: ToastService,
    // Fyin pepper-strengthened PIN: required for the prelogin lookup in fyinFetchPepperIfMigrated.
    private apiService: ApiService,
  ) {
    super(
      i18nService,
      keyService,
      messagingService,
      platformUtilsService,
      policyService,
      dialogService,
      kdfConfigService,
      masterPasswordService,
      accountService,
      toastService,
    );
  }

  togglePassword(confirmField: boolean) {
    this.showPassword = !this.showPassword;
    document.getElementById(confirmField ? "masterPasswordRetype" : "masterPassword").focus();
  }

  async cancel() {
    await this.router.navigate(["/vault"]);
  }

  /**
   * Fyin pepper-strengthened PIN: a peppered reimplementation of the base
   * ChangePasswordComponent.submit(). The base derives the new MasterKey from
   * the raw PIN; a migrated user needs MK = KDF(pin || pepper) so the stored
   * hash and the wrapped user key can be re-derived by peppered login. We
   * override here rather than mutate the shared base Directive. For unmigrated
   * users (fyinPepper == null) the behaviour is identical to the base.
   */
  override async submit() {
    if (!(await this.strongPassword())) {
      return;
    }

    if (!(await this.setupSubmitActions())) {
      return;
    }

    const account = await firstValueFrom(this.accountService.activeAccount$);
    const userId = account?.id;
    const email = account?.email;

    if (this.kdfConfig == null) {
      this.kdfConfig = await this.kdfConfigService.getKdfConfig(userId);
    }

    const fyinPepper = await this.fyinFetchPepperIfMigrated(email);
    const newPasswordForKdf =
      fyinPepper != null ? this.masterPassword + fyinPepper : this.masterPassword;

    const newMasterKey = await this.keyService.makeMasterKey(
      newPasswordForKdf,
      email.trim().toLowerCase(),
      this.kdfConfig,
    );
    const newMasterKeyHash = await this.keyService.hashMasterKey(this.masterPassword, newMasterKey);

    let newProtectedUserKey: [UserKey, EncString] = null;
    const userKey = await this.keyService.getUserKey();
    if (userKey == null) {
      newProtectedUserKey = await this.keyService.makeUserKey(newMasterKey);
    } else {
      newProtectedUserKey = await this.keyService.encryptUserKeyWithMasterKey(newMasterKey);
    }

    await this.performSubmitActions(newMasterKeyHash, newMasterKey, newProtectedUserKey);
  }

  /**
   * Fyin pepper-strengthened PIN: returns the pepper string for a migrated user
   * (so callers can derive MK from `pin || pepper` rather than `pin` alone).
   * Returns null for unmigrated users / errors / pepper-disabled servers.
   */
  private async fyinFetchPepperIfMigrated(email: string): Promise<string | null> {
    try {
      const preloginResponse = await this.apiService.postPrelogin(new PreloginRequest(email));
      if (preloginResponse?.pepperRequired === true && typeof preloginResponse.pepper === "string") {
        return preloginResponse.pepper;
      }
    } catch (e) {
      // 404 / network / etc — fall back to legacy KDF
    }
    return null;
  }

  async setupSubmitActions(): Promise<boolean> {
    if (this.currentMasterPassword == null || this.currentMasterPassword === "") {
      this.toastService.showToast({
        variant: "error",
        title: this.i18nService.t("errorOccurred"),
        message: this.i18nService.t("masterPasswordRequired"),
      });
      return false;
    }

    const secret: Verification = {
      type: VerificationType.MasterPassword,
      secret: this.currentMasterPassword,
    };
    try {
      await this.userVerificationService.verifyUser(secret);
    } catch (e) {
      this.toastService.showToast({
        variant: "error",
        title: this.i18nService.t("errorOccurred"),
        message: e.message,
      });
      return false;
    }
    const userId = await firstValueFrom(getUserId(this.accountService.activeAccount$));
    this.kdfConfig = await this.kdfConfigService.getKdfConfig(userId);
    return true;
  }

  async performSubmitActions(
    newMasterKeyHash: string,
    newMasterKey: MasterKey,
    newUserKey: [UserKey, EncString],
  ) {
    try {
      // Create Request
      const request = new PasswordRequest();
      // Fyin pepper-strengthened PIN: derive the current master key explicitly
      // with the peppered KDF input instead of getOrDeriveMasterKey, which on a
      // cache miss (MASTER_KEY clears on lock) would derive from the raw PIN and
      // produce a proof hash that the server rejects for a migrated user. The
      // outer hashMasterKey() still uses the raw PIN (PasswordLoginStrategy
      // convention). KDF config is unchanged by a password update.
      const proofAccount = await firstValueFrom(this.accountService.activeAccount$);
      const proofEmail = proofAccount?.email;
      const proofPepper = await this.fyinFetchPepperIfMigrated(proofEmail);
      const proofPasswordForKdf =
        proofPepper != null ? this.currentMasterPassword + proofPepper : this.currentMasterPassword;
      const currentMasterKey = await this.keyService.makeMasterKey(
        proofPasswordForKdf,
        proofEmail,
        await this.kdfConfigService.getKdfConfig(proofAccount?.id),
      );
      request.masterPasswordHash = await this.keyService.hashMasterKey(
        this.currentMasterPassword,
        currentMasterKey,
      );
      request.newMasterPasswordHash = newMasterKeyHash;
      request.key = newUserKey[1].encryptedString;

      // Update user's password
      await this.masterPasswordApiService.postPassword(request);

      this.toastService.showToast({
        variant: "success",
        title: this.i18nService.t("masterPasswordChanged"),
        message: this.i18nService.t("logBackIn"),
      });

      if (this.onSuccessfulChangePassword != null) {
        // FIXME: Verify that this floating promise is intentional. If it is, add an explanatory comment and ensure there is proper error handling.
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.onSuccessfulChangePassword();
      } else {
        this.messagingService.send("logout");
      }
    } catch (e) {
      this.logService.error(e);
    }
  }
}
